import React, { useState, useEffect, useRef } from 'react';
import { Phone } from 'lucide-react';
import api from '../services/api';

/**
 * Live Phone Agent call panel for Lead Detail.
 *
 * The webhook bridges the AI receptionist's call onto the org SSE
 * stream as `phone_agent_live` events (status + final transcript
 * utterances, tagged with the matched leadId). This component listens
 * on its own EventSource and renders only when the live call belongs
 * to the lead whose page is open — so an operator watching a lead sees
 * the conversation happen in real time, right where the call note will
 * land afterwards. Renders nothing otherwise.
 */
const PhoneAgentLiveCall = ({ leadId }) => {
  // callId → { number, leadName, status, lines: [{role, text}] }
  const [calls, setCalls] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    setCalls({}); // navigating to another lead clears the panel
    const token = localStorage.getItem('token');
    if (!token || !leadId) return undefined;
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    const es = new EventSource(`${base}/events?token=${encodeURIComponent(token)}`);
    es.onmessage = (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch (_) { return; }
      if (data?.type !== 'phone_agent_live') return;
      if (String(data.leadId || '') !== String(leadId)) return;
      setCalls((prev) => {
        const call = prev[data.callId] || { number: data.number, leadName: data.leadName, status: 'in-progress', lines: [] };
        const next = { ...call, number: data.number || call.number };
        if (data.event === 'status') {
          next.status = data.status;
          if (data.status === 'ended') {
            // Linger briefly so the operator sees the close, then clear —
            // the permanent record lands on the timeline via the
            // end-of-call webhook moments later.
            setTimeout(() => {
              setCalls((p) => {
                const { [data.callId]: gone, ...rest } = p;
                return rest;
              });
            }, 10000);
          }
        } else if (data.event === 'transcript') {
          next.lines = [...call.lines.slice(-60), { role: data.role, text: data.text }];
        }
        return { ...prev, [data.callId]: next };
      });
    };
    return () => es.close();
  }, [leadId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [calls]);

  const entries = Object.entries(calls);
  if (entries.length === 0) return null;

  return (
    <div className="bg-white border-2 border-red-200 rounded-xl shadow-sm mb-6 overflow-hidden">
      <div className="px-5 py-3 border-b border-red-100 bg-red-50 flex items-center gap-2">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
        <h2 className="text-sm font-semibold text-red-900">Live call — AI receptionist</h2>
      </div>
      <div className="p-4 space-y-4">
        {entries.map(([callId, call]) => (
          <div key={callId} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-2 text-sm">
              <Phone size={14} className="text-gray-500" />
              {call.number && <span className="font-mono text-xs text-gray-500">{call.number}</span>}
              <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-medium ${
                call.status === 'ended' ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-800'
              }`}>
                {call.status === 'ended' ? 'Ended' : '● In progress'}
              </span>
            </div>
            <div ref={scrollRef} className="max-h-56 overflow-y-auto p-3 space-y-1.5 bg-white">
              {call.lines.length === 0 && (
                <p className="text-xs text-gray-400 italic">Waiting for the conversation…</p>
              )}
              {call.lines.map((l, i) => (
                <div key={i} className={`flex ${l.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                  <span className={`inline-block px-2.5 py-1.5 rounded-lg text-xs leading-relaxed max-w-[85%] ${
                    l.role === 'assistant'
                      ? 'bg-teal-50 text-teal-900 border border-teal-100'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {l.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PhoneAgentLiveCall;
